import { useEffect, useState } from 'react'

// Injected at build time by vite.config.js. Regenerated on every build.
// eslint-disable-next-line no-undef
const LOCAL_BUILD_ID = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'

// Poll frequency. Two minutes is snappy enough that most users see the
// update within a couple minutes of deploy, and low enough that 16 clients
// polling create negligible load.
const CHECK_INTERVAL_MS = 2 * 60 * 1000

/**
 * useVersionCheck — polls the static /version.json file that each build
 * emits, and signals when the remote buildId has moved past the one
 * that was bundled into the currently-running JS.
 *
 * Returns:
 *   isStale — true once we detect a newer version is live on the server
 *
 * Also re-checks when the tab regains focus, so users who leave the tab
 * open overnight get prompted on return without waiting for the poll.
 */
export function useVersionCheck() {
  const [isStale, setIsStale] = useState(false)
  const [releaseNote, setReleaseNote] = useState('')

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      try {
        // Relative path so it works under /wbr/ (production) and /
        // (localhost dev). Cache-busted query so we bypass any HTTP
        // caching — we always want the truly latest deployed version.
        const res = await fetch('version.json?t=' + Date.now(), {
          cache: 'no-store',
        })
        if (!res.ok) return
        const data = await res.json()
        if (cancelled) return
        if (data.buildId && data.buildId !== LOCAL_BUILD_ID) {
          setIsStale(true)
          // Pick up the release note the deploying dev included for
          // this build so the banner can explain what's new.
          if (typeof data.releaseNote === 'string') {
            setReleaseNote(data.releaseNote)
          }
        }
      } catch {
        // Network hiccup — try again on the next interval
      }
    }

    // Kick off first check ~10s after mount so we don't race the page load
    const initialTimer = setTimeout(check, 10_000)
    const interval = setInterval(check, CHECK_INTERVAL_MS)

    // Re-check when tab regains focus
    const onFocus = () => check()
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      clearTimeout(initialTimer)
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  return { isStale, releaseNote, localBuildId: LOCAL_BUILD_ID }
}
